import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import Button from '../components/ui/Button';
import FieldSelect from '../components/ui/FieldSelect';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';
import {
  ROUTE_REASON_LABELS,
  missingFieldLabel,
  qualityLabel,
  routeLabel,
  routeReasonLabel,
  sourceLabel,
  stateLabel,
} from '../utils/equipmentQuality';
import type { EquipmentInventoryResponse, EquipmentInventoryRow } from '../types';

/**
 * Plan 119 Phase 2 (FS-460) — the equipment inventory, as an operator works
 * it.
 *
 * One row per (device, field): do we have it, where did it come from, how
 * good is it, and what happens next. Operations work the "Waiting on us"
 * and "Needs a look" rows; the email campaign (FS-600) takes "Ask the
 * homeowner".
 *
 * Everything shown here is computed by the server on read, so the wording
 * matches the Device List and the device Overview exactly.
 */

/**
 * The row and response shapes moved to `types/` in this change: the device
 * workspace reads the same endpoint for a single device, and one declaration
 * is the only way the two screens cannot drift apart.
 */
type InventoryRow = EquipmentInventoryRow;
type InventoryResponse = EquipmentInventoryResponse;

const PAGE_SIZE = 200;

const FIELD_OPTIONS = [
  'tonnage',
  // FS-752 — the furnace or air handler record: a model number the catalog
  // resolved to an indoor unit, with a manual on file.
  'indoor_record',
  'motor_type',
  'filter_curve',
  'rated_static',
  'outdoor_photo',
  'hvac_photo',
  'filter_photo',
  // Plan 120 — the equipment record's own row: the legacy guard is holding
  // part of this system's record, and only a person can settle where the held
  // facts belong. Filterable like any other field so the hold queue is one
  // filter rather than a search.
  'hvac_component',
];
// `conflict` arrived with the equipment model catalog (FS-627): a value is
// recorded and a manufacturer document contradicts it.
const STATE_OPTIONS = [
  'known',
  'weak',
  'conflict',
  'missing',
  'unknown',
  'not_applicable',
  'unavailable',
];
const ROUTE_OPTIONS = ['none', 'back_end_recoverable', 'nudge_user', 'ops_contact', 'not_applicable'];

/** Which part of the system a value came off, in words. */
const COMPONENT_LABELS: Record<string, string> = {
  outdoor_condenser: 'outdoor unit',
  indoor_air_handler: 'air handler',
  indoor_coil: 'indoor coil',
  package_unit: 'packaged unit',
  filter: 'filter',
  outdoor: 'outdoor unit',
  hvac: 'furnace or air handler',
};
const componentLabel = (c: string | null) => (c ? COMPONENT_LABELS[c] ?? c : 'unknown');

/**
 * Why a row needs attention, spelled out. The map moved to
 * `utils/equipmentQuality` in Plan 119 Phase 3 (FS-627) — the device Overview
 * now says the same things about a missing rated pressure, and one map is the
 * only way the two screens cannot word it differently. Re-exported here
 * because this page is where the reasons are read in bulk.
 */
export const REASON_LABELS = ROUTE_REASON_LABELS;
export const reasonLabel = (r: string) => routeReasonLabel(r);

const ROUTE_TINT: Record<string, string> = {
  none: 'border-status-neutral-border bg-status-neutral text-status-neutral-foreground',
  back_end_recoverable: 'border-status-info-border bg-status-info text-status-info-foreground',
  nudge_user: 'border-status-warning-border bg-status-warning text-status-warning-foreground',
  ops_contact: 'border-status-error-border bg-status-error text-status-error-foreground',
  not_applicable: 'border-status-neutral-border bg-status-neutral text-status-neutral-foreground',
};

const dash = <span className="text-gray-600">unknown</span>;
const text = (v: string | null) => (v ? v : dash);

/**
 * The device lookup takes digits and nothing else. Anything that is not a
 * positive whole number is no lookup at all, and clears rather than sending
 * the server something it will reject.
 */
const normalizeDeviceId = (raw: string | null): string => {
  const digits = (raw ?? '').replace(/[^0-9]/g, '').replace(/^0+/, '');
  return digits.length > 0 ? digits : '';
};

const EquipmentInventoryPage: React.FC = () => {
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  // Three distinguishable failures: not allowed, everything else, and the
  // ordinary empty result (which is not a failure at all).
  const [error, setError] = useState<'forbidden' | 'failed' | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [cohort, setCohort] = useState('installed');
  const [field, setField] = useState('');
  const [state, setState] = useState('');
  const [route, setRoute] = useState('');

  /**
   * One device at a time. `?device_id=` on the URL is how the device
   * workspace links in, so the lookup starts from the query string and is
   * written back to it — a filtered page can be linked to and reloaded.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const [deviceId, setDeviceId] = useState(() => normalizeDeviceId(searchParams.get('device_id')));
  const [deviceIdInput, setDeviceIdInput] = useState(deviceId);

  /**
   * A lookup does not widen the cohort by itself — a device with no install
   * date is only in `all` — so it forces `all` for as long as it is set. The
   * operator's own choice is kept untouched underneath and comes back when
   * the lookup is cleared.
   */
  const effectiveCohort = deviceId ? 'all' : cohort;

  const params = useMemo(() => {
    const p: Record<string, string | number> = {
      cohort: effectiveCohort,
      limit: PAGE_SIZE,
      offset,
    };
    if (deviceId) p['f.device_id'] = Number(deviceId);
    if (field) p['f.field'] = field;
    if (state) p['f.state'] = state;
    if (route) p['f.route'] = route;
    return p;
  }, [effectiveCohort, deviceId, field, state, route, offset]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get<InventoryResponse>(
        `${config.devicesApiUrl}/devices/inventory`,
        { params },
      );
      setRows(resp.data.rows);
      setTotal(resp.data.total);
    } catch (err: any) {
      const status = err?.response?.status;
      setError(status === 401 || status === 403 ? 'forbidden' : 'failed');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  /**
   * The export is behind the same bearer token as the rest of the API, and a
   * plain <a href> cannot carry an Authorization header, so the file is
   * fetched as a blob through the shared axios instance and handed to the
   * browser from an object URL. (AnalyzePanel's CSV button can use a plain
   * link because it downloads a presigned S3 URL, which carries its own
   * credentials in the query string; this endpoint does not.)
   */
  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const resp = await axiosInstance.get(`${config.devicesApiUrl}/devices/inventory.csv`, {
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      const day = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `equipment-inventory-${day}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('The export could not be downloaded. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const onFilter = (setter: (v: string) => void) => (v: string) => {
    setOffset(0);
    setter(v);
  };

  /** Apply whatever is in the lookup box, and put it on the URL. */
  const applyLookup = useCallback(
    (raw: string) => {
      const next = normalizeDeviceId(raw);
      setDeviceIdInput(next);
      if (next === deviceId) return;
      setDeviceId(next);
      setOffset(0);
      const nextParams = new URLSearchParams(searchParams);
      if (next) nextParams.set('device_id', next);
      else nextParams.delete('device_id');
      setSearchParams(nextParams, { replace: true });
    },
    [deviceId, searchParams, setSearchParams],
  );

  const shown = rows.length;
  const firstOnPage = total === 0 ? 0 : offset + 1;

  return (
    <div className="container mx-auto p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h1 className="text-2xl font-bold">Equipment Inventory</h1>
        <Button
          variant="primary"
          onClick={exportCsv}
          disabled={exporting || loading}
          title="Download the rows that match the filters below as a CSV file."
        >
          {exporting ? 'Preparing…' : 'Export CSV'}
        </Button>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        One row per device per piece of equipment information: what we have,
        how good it is, and who does the next thing. FluxHive works the rows
        marked “Waiting on us” and “Needs a look”; the email campaign takes
        “Ask the homeowner”.
      </p>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="flex flex-col">
          <Label htmlFor="inv-device-id">Device ID</Label>
          <Input
            id="inv-device-id"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="e.g. 116"
            className="mt-1 w-28"
            value={deviceIdInput}
            aria-describedby="inv-device-id-help"
            onChange={(e) => setDeviceIdInput(normalizeDeviceId(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLookup(e.currentTarget.value);
              }
            }}
            onBlur={(e) => applyLookup(e.target.value)}
          />
          <p id="inv-device-id-help" className="text-xs text-muted-foreground">Enter a device ID to show its inventory rows. Enter or leave the field to apply.</p>
        </div>
        {(deviceId || deviceIdInput) && (
          <Button
            variant="secondary"
            aria-label="Clear device lookup"
            onClick={() => applyLookup('')}
            title="Empty the Device ID box and show every device again."
          >
            Clear
          </Button>
        )}
        <div className="flex flex-col">
          <FieldSelect
            id="inv-cohort"
            label="Devices"
            help="Choose installed devices or every device. Device ID lookup always searches every device."
            value={effectiveCohort}
            onChange={onFilter(setCohort)}
            disabled={!!deviceId}
            options={[
              { value: 'installed', label: 'Installed only' },
              { value: 'all', label: 'Every device' },
            ]}
          />
          {deviceId && (
            <span className="mt-1 text-xs text-muted-foreground">
              A device lookup searches every device.
            </span>
          )}
        </div>
        <FieldSelect
          id="inv-field"
          label="Information"
          help="Show rows for one kind of equipment information."
          value={field}
          onChange={onFilter(setField)}
          options={[
            { value: '', label: 'Anything' },
            ...FIELD_OPTIONS.map((f) => ({ value: f, label: missingFieldLabel(f) })),
          ]}
        />
        <FieldSelect
          id="inv-state"
          label="Do we have it"
          help="Filter by whether the information is known, weak, missing or another state."
          value={state}
          onChange={onFilter(setState)}
          options={[
            { value: '', label: 'Any answer' },
            ...STATE_OPTIONS.map((s) => ({ value: s, label: stateLabel(s) })),
          ]}
        />
        <FieldSelect
          id="inv-route"
          label="What is missing"
          help="Filter by the next step needed to complete the information."
          value={route}
          onChange={onFilter(setRoute)}
          options={[
            { value: '', label: 'Any next step' },
            ...ROUTE_OPTIONS.map((r) => ({ value: r, label: routeLabel(r) })),
          ]}
        />
        <Button
          variant="secondary"
          onClick={load}
          disabled={loading}
          title="Ask the device API for this list again, with the same filters."
        >
          Refresh
        </Button>
      </div>

      {exportError && <p className="mb-3 text-status-error-foreground text-sm">{exportError}</p>}

      {loading && <p className="text-muted-foreground">Loading…</p>}
      {!loading && error === 'forbidden' && (
        <p className="text-status-error-foreground">
          You do not have permission to read the equipment inventory. Ask an
          administrator for device read access.
        </p>
      )}
      {!loading && error === 'failed' && (
        <p className="text-status-error-foreground">
          The equipment inventory could not be loaded. Try Refresh; if it keeps
          failing the device API is not responding.
        </p>
      )}
      {!loading && !error && total === 0 && (
        <p className="text-muted-foreground">
          {deviceId
            ? `No inventory rows for device ${deviceId} in scope`
            : 'Nothing matches these filters'}
        </p>
      )}

      {!loading && !error && total > 0 && (
        <>
          <p className="text-sm text-gray-600 mb-2">
            Showing {firstOnPage}–{offset + shown} of {total}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  {/* The id leads: it is what an operator types into the
                      lookup, what the device URL is keyed on, and what a
                      report or a ticket names a device by. */}
                  <th scope="col" className="text-left py-2 px-3 border-b">Device ID</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">Device</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">Information</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">Do we have it</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">Value</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">How good</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">Where from</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">Part</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">Next step</th>
                  <th scope="col" className="text-left py-2 px-3 border-b">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.device_id}-${r.field}`} className="hover:bg-gray-50 border-b">
                    <td className="py-2 px-3">
                      <Link
                        to={`/devices/${r.device_id}`}
                        className="text-blue-700 hover:underline font-mono"
                      >
                        {r.device_id}
                      </Link>
                    </td>
                    <td className="py-2 px-3">
                      <Link
                        to={`/devices/${r.device_id}`}
                        className="text-blue-700 hover:underline font-mono"
                      >
                        {r.device_name}
                      </Link>
                      {r.shared_record && (
                        <span
                          className="ml-2 text-xs px-1.5 py-0.5 rounded-sm border bg-red-50 border-status-error-border text-red-800"
                          title="More than one device on this app record reports a different filter size, so these are probably two systems recorded as one."
                        >
                          shared record
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3">{missingFieldLabel(r.field)}</td>
                    <td className="py-2 px-3">{stateLabel(r.state)}</td>
                    <td className="py-2 px-3">
                      {r.value ? `${r.value}${r.unit && r.unit !== 'photos' ? ` ${r.unit}` : ''}` : dash}
                    </td>
                    <td className="py-2 px-3">{qualityLabel(r.quality)}</td>
                    <td className="py-2 px-3">{sourceLabel(r.source)}</td>
                    <td className="py-2 px-3">{componentLabel(r.component)}</td>
                    <td className="py-2 px-3">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded-sm border ${ROUTE_TINT[r.route] ?? ''}`}
                      >
                        {routeLabel(r.route)}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-1">
                        {reasonLabel(r.route_reason)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-600">{text(r.evidence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <Button
              variant="secondary"
              onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
              disabled={offset === 0}
              title="Go back one page of 200 rows."
            >
              Previous page
            </Button>
            <Button
              variant="secondary"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + shown >= total}
              title="Go on to the next page of 200 rows."
            >
              Next page
            </Button>

          </div>
        </>
      )}
    </div>
  );
};

export default EquipmentInventoryPage;
